// @ts-nocheck
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Instance } from 'mobx-state-tree';
import { OrderStatus } from './OrderStatusEnum';
import { SubscriptionEntityModelBase } from './SubscriptionEntityModel.base';
import { Frequency } from '@/models/api/FrequencyEnum';
import { DeliveryStatus, DEFAULT_TIMEZONE } from '@/utils/constants';

import _ from 'lodash';
import { DELIVERY_DATE_FORMAT } from '@/shared/constant';
import { ShipNowOrder } from '@/models/api/ShipNowOrderEnum';
import {
  SubscriptionStatus,
  SubscriptionStatusEnumType,
} from '@/models/api/SubscriptionStatusEnum';

dayjs.extend(utc);
dayjs.extend(timezone);

/* The TypeScript type of an instance of SubscriptionEntityModel */
export interface SubscriptionEntityModelType
  extends Instance<typeof SubscriptionEntityModel> {}

/* A graphql query fragment builders for SubscriptionEntityModel */
export {
  selectFromSubscriptionEntity,
  subscriptionEntityModelPrimitives,
  SubscriptionEntityModelSelector,
} from './SubscriptionEntityModel.base';

export const FREQUENCY_BUFFER = {
  [Frequency.ONCE]: 0,
  [Frequency.WEEKLY]: 7,
  [Frequency.FORTNIGHTLY]: 14,
  [Frequency.MONTHLY]: 28,
};

export const FREQUENCY_LABELS = {
  [Frequency.ONCE]: 'One time',
  [Frequency.WEEKLY]: 'Every Week',
  [Frequency.FORTNIGHTLY]: 'Every 2 Weeks',
  [Frequency.MONTHLY]: 'Every 4 Weeks',
};
/**
 * SubscriptionEntityModel
 *
 * Subscription
 */
export const SubscriptionEntityModel = SubscriptionEntityModelBase.actions(
  (self) => ({
    // This is an auto-generated example action.
    log() {
      console.log(JSON.stringify(self));
    },
  }),
).views((self) => ({
  get deliveryAddress() {
    return self.delivery.deliveryAddress.fullAddress;
  },
  get deliveryAddressInfo() {
    return {
      postcode: self.delivery.deliveryAddress.postcode,
      suburb: self.delivery.deliveryAddress.suburb,
    };
  },
  get showCurrentDelivery() {
    return (
      self.currentOrder &&
      self.currentOrder.orderStatus === OrderStatus.PROCESSED &&
      dayjs().isBefore(self.currentOrder.delivery.deliveryDate)
    );
  },
  get formattedDeliveryDate() {
    return dayjs.utc(self.delivery.deliveryDate).format(DELIVERY_DATE_FORMAT);
  },
  get nextDeliveryWeekDay() {
    return dayjs.utc(self.delivery.deliveryDate).format('dddd');
  },
  get cutOffDate() {
    return dayjs(self.chargeDate)
      .tz(DEFAULT_TIMEZONE)
      .add(1, 'day')
      .startOf('day');
  },
  get isEditable() {
    return this.cutOffDate.isAfter(dayjs());
  },
  get frequencyBuffer() {
    return FREQUENCY_BUFFER[self.frequency as Frequency];
  },
  get deliveries(): {
    date: string;
    status: string;
    deliveryStatus: string;
    deliveryDate: string;
    timeSlot: string;
    startDate?: string;
    chargeDate?: string;
  }[] {
    const SHORT_FORMAT = 'D MMM';
    const LONG_FORMAT = 'ddd, D MMM YYYY'; //Tue, 29 Oct 2022, Midnight - 7am
    const currentDelivery = self.currentOrder?.delivery;
    const timeSlot = self.delivery.timeSlot;
    const buffer = this.frequencyBuffer;
    const isEditable = this.isEditable;
    const cutOffDate = this.cutOffDate;
    const dates = [
      {
        date: dayjs(currentDelivery.deliveryDate).format(SHORT_FORMAT),
        status: self.currentOrder?.orderStatus,
        deliveryStatus: DeliveryStatus.CURRENT,
        deliveryDate: dayjs(currentDelivery.deliveryDate).format(LONG_FORMAT),
        chargeDate: dayjs(currentDelivery.chargeDate).format(LONG_FORMAT),
        timeSlot: currentDelivery.timeSlot,
        startDate: '',
      },
      ..._.map(self.futures, (future, index) => {
        const lastCutOffDate = cutOffDate
          .add((index - 1) * buffer, 'day')
          .format(SHORT_FORMAT);
        const nextChargeDate = dayjs(self.chargeDate)
          .add(index * buffer, 'day')
          .format(LONG_FORMAT);
        const startDate = !index ? '' : lastCutOffDate;

        return {
          date: future.shortDeliveryDate,
          status: future.status,
          isEditable: !index && isEditable,
          active: !!index || isEditable,
          deliveryStatus: !index ? DeliveryStatus.NEXT : DeliveryStatus.FUTURE,
          deliveryDate: future.longDeliveryDate,
          chargeDate: nextChargeDate,
          timeSlot,
          startDate,
        };
      }),
    ];
    return this.showCurrentDelivery ? dates.slice(0, 4) : dates.slice(1);
  },
  get isUpcomingOrderShipNow() {
    return self.shipNowOrder === ShipNowOrder.UPCOMING_ORDER;
  },
  get shipNowDelivery() {
    return this.isUpcomingOrderShipNow
      ? self.delivery
      : self.currentOrder?.delivery;
  },
  get formattedShipNowDeliveryDate() {
    return dayjs
      .utc(this.shipNowDelivery?.deliveryDate)
      .format(DELIVERY_DATE_FORMAT);
  },
  get timeSlot() {
    return this.shipNowDelivery?.timeSlot;
  },
  get frequencyLabel() {
    return FREQUENCY_LABELS[self.frequency as Frequency];
  },
  get allSkus() {
    const skus = _.flatMap(self.plans, (p) => _.map(p.lineItems, 'sku'));
    return _.uniq(skus);
  },
}));
